import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// أنواع قوالب الرسائل
export type WhatsAppTemplateType = 'deviceReceived' | 'deviceInProgress' | 'deviceCompleted' | 'deviceUrgent';

// واجهة قالب الرسالة
export interface WhatsAppTemplate {
  id: WhatsAppTemplateType;
  name: string;
  description: string;
  content: string;
  variables: string[];
  enabled: boolean;
  lastModified: Date;
}

// القوالب الافتراضية المقصرة
const defaultTemplates: Record<WhatsAppTemplateType, WhatsAppTemplate> = {
  deviceReceived: {
    id: 'deviceReceived',
    name: 'رسالة استلام الجهاز',
    description: 'ترسل عند استلام الجهاز من العميل',
    content: `🔧 تم استلام جهازك بنجاح

مرحباً {customerName}
تم استلام {deviceBrand} - {deviceSize}

رقم الطلب: #{orderId}
التكلفة: {cost}

سنتواصل معك قريباً بالتكلفة النهائية
📞 {phoneNumber}`,
    variables: ['{customerName}', '{deviceBrand}', '{deviceSize}', '{cost}', '{orderId}', '{receiveDate}', '{phoneNumber}', '{address}'],
    enabled: true,
    lastModified: new Date()
  },

  deviceInProgress: {
    id: 'deviceInProgress',
    name: 'رسالة التكلفة أثناء العمل',
    description: 'ترسل للعملاء الذين يطلبون الاتصال قبل الإصلاح',
    content: `⚙️ تحديث حالة الإصلاح

مرحباً {customerName}
تم فحص {deviceBrand} - {deviceSize}

رقم الطلب: #{orderId}
التكلفة النهائية: {cost}

يرجى الرد بـ "موافق" للمتابعة أو "رفض" لإرجاع الجهاز
📞 {phoneNumber}`,
    variables: ['{customerName}', '{deviceBrand}', '{deviceSize}', '{cost}', '{orderId}', '{problemDescription}', '{phoneNumber}'],
    enabled: true,
    lastModified: new Date()
  },

  deviceCompleted: {
    id: 'deviceCompleted',
    name: 'رسالة اكتمال الإصلاح',
    description: 'ترسل عند اكتمال إصلاح الجهاز',
    content: `✅ تم الانتهاء من إصلاح جهازك!

مرحباً {customerName}
جهازك {deviceBrand} - {deviceSize} جاهز للاستلام

رقم الطلب: #{orderId}
التكلفة: {cost}

يرجى المرور لاستلام جهازك
📞 {phoneNumber}
📍 {address}`,
    variables: ['{customerName}', '{deviceBrand}', '{deviceSize}', '{orderId}', '{cost}', '{completionDate}', '{address}', '{phoneNumber}'],
    enabled: true,
    lastModified: new Date()
  },

  deviceUrgent: {
    id: 'deviceUrgent',
    name: 'رسالة الاستعجال',
    description: 'ترسل تلقائياً للأجهزة المكتملة غير المستلمة كل 48 ساعة',
    content: `🚨 تذكير عاجل - استلام الجهاز

مرحباً {customerName}
جهازك {deviceBrand} جاهز للاستلام منذ {daysSinceCompletion} أيام

رقم الطلب: #{orderId}
التكلفة: {cost}

يرجى المرور لاستلام جهازك قريباً
📞 {phoneNumber}`,
    variables: ['{customerName}', '{deviceBrand}', '{deviceSize}', '{orderId}', '{cost}', '{completionDate}', '{daysSinceCompletion}', '{address}', '{phoneNumber}'],
    enabled: true,
    lastModified: new Date()
  }
};

class WhatsAppTemplatesService {
  private readonly COLLECTION_NAME = 'whatsapp_templates';
  private readonly DOC_ID = 'templates_config';

  // الحصول على جميع القوالب
  public async getTemplates(): Promise<Record<WhatsAppTemplateType, WhatsAppTemplate>> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, this.DOC_ID);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return { ...defaultTemplates, ...data.templates };
      }

      // إذا لم توجد القوالب، احفظ القوالب الافتراضية
      await this.saveTemplates(defaultTemplates);
      return defaultTemplates;
    } catch (error) {
      console.error('Error getting WhatsApp templates:', error);
      return defaultTemplates;
    }
  }

  // الحصول على قالب واحد
  public async getTemplate(type: WhatsAppTemplateType): Promise<WhatsAppTemplate> {
    const templates = await this.getTemplates();
    return templates[type] || defaultTemplates[type];
  }

  // حفظ جميع القوالب
  public async saveTemplates(templates: Record<WhatsAppTemplateType, WhatsAppTemplate>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, this.DOC_ID);
      await setDoc(docRef, {
        templates,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error saving WhatsApp templates:', error);
      throw error;
    }
  }

  // تحديث قالب واحد
  public async updateTemplate(type: WhatsAppTemplateType, template: Partial<WhatsAppTemplate>): Promise<void> {
    try {
      const templates = await this.getTemplates();
      templates[type] = {
        ...templates[type],
        ...template,
        lastModified: new Date()
      };
      await this.saveTemplates(templates);
    } catch (error) {
      console.error('Error updating WhatsApp template:', error);
      throw error;
    }
  }

  // معاينة قالب مع استبدال المتغيرات
  public previewTemplate(template: WhatsAppTemplate, variables: Record<string, string>): string {
    let content = template.content;
    
    // استبدال المتغيرات
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(key.replace(/[{}]/g, '\\$&'), 'g');
      content = content.replace(regex, value || 'غير محدد');
    });

    return content;
  }

  // إعادة تعيين القوالب للافتراضية
  public async resetToDefaults(): Promise<void> {
    await this.saveTemplates(defaultTemplates);
  }

  // الحصول على المتغيرات المتاحة لقالب معين
  public getAvailableVariables(type: WhatsAppTemplateType): string[] {
    return defaultTemplates[type].variables;
  }

  // التحقق من صحة القالب
  public validateTemplate(template: WhatsAppTemplate): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!template.content || template.content.trim().length === 0) {
      errors.push('محتوى القالب مطلوب');
    }

    if (template.content && template.content.length > 1000) {
      errors.push('محتوى القالب طويل جداً (الحد الأقصى 1000 حرف)');
    }

    if (!template.name || template.name.trim().length === 0) {
      errors.push('اسم القالب مطلوب');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // الحصول على إحصائيات القوالب
  public async getTemplateStats(): Promise<Record<WhatsAppTemplateType, { enabled: boolean; lastModified: Date; length: number }>> {
    const templates = await this.getTemplates();
    const stats: Record<string, any> = {};

    Object.entries(templates).forEach(([type, template]) => {
      stats[type] = {
        enabled: template.enabled,
        lastModified: template.lastModified,
        length: template.content.length
      };
    });

    return stats as Record<WhatsAppTemplateType, { enabled: boolean; lastModified: Date; length: number }>;
  }

  // حفظ قالب واحد (للتوافق مع الكود الموجود)
  public async saveTemplate(template: WhatsAppTemplate): Promise<void> {
    await this.updateTemplate(template.id, template);
  }

  // حفظ جميع القوالب (للتوافق مع الكود الموجود)
  public async saveAllTemplates(templates: Record<WhatsAppTemplateType, WhatsAppTemplate>): Promise<void> {
    await this.saveTemplates(templates);
  }

  // إعادة تعيين قالب واحد (للتوافق مع الكود الموجود)
  public async resetTemplate(type: WhatsAppTemplateType): Promise<void> {
    await this.updateTemplate(type, defaultTemplates[type]);
  }

  // تبديل حالة تفعيل القالب (للتوافق مع الكود الموجود)
  public async toggleTemplate(type: WhatsAppTemplateType, enabled: boolean): Promise<void> {
    await this.updateTemplate(type, { enabled });
  }

  // تحديث القوالب لماركة الجهاز (للتوافق مع الكود الموجود)
  public async updateTemplatesForDeviceBrand(): Promise<void> {
    // هذه الدالة للتوافق فقط - لا تحتاج لتنفيذ خاص
    console.log('updateTemplatesForDeviceBrand called - no action needed');
  }
}

// إنشاء مثيل واحد من الخدمة
export const whatsappTemplatesService = new WhatsAppTemplatesService();

// تصدير القوالب الافتراضية للاستخدام في أماكن أخرى
export { defaultTemplates };

export default whatsappTemplatesService;
